import { Alert, List, Paper, Stack } from '@mui/material'
import React, { FC, useContext, useMemo, useState } from 'react'

import useAccessTokenAndShopId from '../../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { RepairOrderContext } from '../../../providers/RepairOrderProvider/RepairOrderProvider'
import { useGetVehicleDetailQuery } from '../../../redux/api'
import { CannedJobExpanded } from '../../../sdk'
import { getMatchedCannedJobs } from '../../../utils/getMatchedCannedJobs/getMatchedCannedJobs'
import { sortCannedJobExpandedByName } from '../../../utils/sortCannedJobByName'
import { QuickSearch } from '../../SharedUI/QuickSearch'
import { SortSelect, SortTypes } from '../../SharedUI/SortSelect'
import { CannedJobItem } from '../CannedJobListItem/CannedJobItem'
import { SelectedJob } from './transformSelectedJob'
interface Props {
  cannedJobsExpanded: CannedJobExpanded[]
  selected: SelectedJob
  setSelected: (selected: SelectedJob) => void
}
export const SearchCannedJob: FC<Props> = ({
  cannedJobsExpanded,
  selected,
  setSelected
}) => {
  const [sortOrder, setSortOrder] = useState<SortTypes>(
    (localStorage.getItem('searchCannedJobSortOrder') as SortTypes) || 'az'
  )
  const [keyword, setKeyword] = useState('')
  const { accessToken, shopId } = useAccessTokenAndShopId()
  const { repairOrder } = useContext(RepairOrderContext)
  const vehicleId = repairOrder!.entry!.info!.vehicle!
  const { data: vehicles } = useGetVehicleDetailQuery(
    { authorization: accessToken, shop: shopId, vehicle: [vehicleId!] },
    { skip: !vehicleId }
  )
  const vehicle = vehicles && vehicles[0]
  const vehicleHasValidMakeAndModel = useMemo(() => {
    if (!vehicle) {
      return false
    }
    return (
      vehicle.info!.make?.id !== undefined &&
      vehicle.info!.model?.id !== undefined
    )
  }, [vehicle])

  const cannedJobsToDisplay = useMemo(
    () =>
      vehicleHasValidMakeAndModel
        ? cannedJobsExpanded
        : cannedJobsExpanded.filter((canned) => {
            if (canned.motorFluids && canned.motorFluids.length > 0) {
              return false
            }
            if (canned.motorLabor && canned.motorLabor.length > 0) {
              return false
            }
            return true
          }),
    [cannedJobsExpanded, vehicleHasValidMakeAndModel]
  )

  const filteredJobs = useMemo(
    () => getMatchedCannedJobs(keyword, cannedJobsToDisplay),
    [cannedJobsToDisplay, keyword]
  )

  const sortedJobs = useMemo(() => {
    if (sortOrder === 'az') {
      return sortCannedJobExpandedByName(filteredJobs)
    }
    return filteredJobs
  }, [filteredJobs, sortOrder])

  return (
    <>
      <Stack
        direction="row"
        justifyContent="flex-end"
        mb={1}
        spacing={1}
        alignItems="flex-end">
        <SortSelect
          {...{ sortOrder, setSortOrder }}
          options={['lastUpdated', 'az']}
          localStorageName="searchCannedJobSortOrder"
        />
        <QuickSearch
          value={keyword}
          autoFocus={true}
          onChange={(event) => setKeyword(event.target.value)}
          clearField={() => setKeyword('')}
        />
      </Stack>

      <Paper
        component={List}
        variant="outlined"
        sx={{
          overflowY: 'auto',
          height: '100%',
          boxSizing: 'border-box'
        }}
        disablePadding>
        {!vehicleHasValidMakeAndModel && (
          <Alert severity="warning">
            Some canned jobs may be unavailable for custom vehicles
          </Alert>
        )}
        {sortedJobs.map((canned) => (
          <CannedJobItem
            key={canned.entry!.id!}
            selected={selected}
            canned={canned}
            setSelected={setSelected}
          />
        ))}
      </Paper>
    </>
  )
}
